import type { SystemRole } from '@repo/contracts';
import { Button } from '@repo/ui';

import { APP_ROUTES } from '@/constants/app-routes';
import { systemRoleLabel } from '@/constants/labels';
import { appRolesOf } from '@/hooks/auth/app-roles';
import { useSetActiveRole } from '@/hooks/auth/use-set-active-role';
import { toastApiError } from '@/shared/api/toast-api-error';
import { announceSession } from '@/shared/utils/session-channel';

interface SwitchToAccessRoleButtonProps {
  heldRoles: readonly SystemRole[];
}

/**
 * The session authorizes with one active role, so an administrator whose
 * session is on a delivery role lands on the no-access page with no shell to
 * switch from. A role they hold that can open this app is offered here.
 */
export function SwitchToAccessRoleButton({ heldRoles }: SwitchToAccessRoleButtonProps) {
  const { mutate: switchRole, isPending } = useSetActiveRole();
  const [target] = appRolesOf(heldRoles);

  if (!target) return null;

  const onSwitch = () =>
    switchRole(target, {
      onSuccess: () => {
        announceSession('role-switched');
        window.location.assign(APP_ROUTES.dashboard);
      },
      onError: toastApiError,
    });

  return (
    <Button onClick={onSwitch} disabled={isPending}>
      Switch to {systemRoleLabel(target)}
    </Button>
  );
}
